import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useSearch } from '@tanstack/react-router';
import { SideNav } from '@yembo/yemblocks-core';
import { useEffect } from 'react';
import { initializeLogRocket } from 'src/analytics/logRocket';
import { initAuthUser } from 'src/api/yembo/employee';
import { useEmployeeQuery } from 'src/queries/Employee';
import {
  useCompaniesWithEmployeePreferencesQuery,
  useCompanyQuery,
  useFirstCompanyKeyQuery,
} from 'src/queries/Company';
import { EMPLOYEE_QUERY_KEY, COMPANY_QUERY_KEY, FIRST_COMPANY_KEY, LIST_KEY } from 'src/queries/queryKeys';
import { updateAuth, useAuth } from 'src/util/AuthModule';
import { useWindowDimensions } from '../_hooks/useWindowDimensions';
import { AuthUserContext } from '../_shared/Context/AuthUserContext';
import { LoadingAnimation } from '../_shared/LoadingAnimation';
import { useAutomaticLogout } from '../_sw/useAutomaticLogout';
import { SIDEBAR_OPEN_THRESHOLD } from '../_util/ScreenConstants';

export const PrivateRoute = () => {
  const queryClient = useQueryClient();
  const { accessToken, employeeKey, deviceKey } = useAuth();
  const hasAccessCredentials = !!(accessToken && employeeKey && deviceKey);

  if (!hasAccessCredentials) {
    const { pathname, search, hash } = window.location;
    const searchParams = new URLSearchParams(search);

    const nextPage = searchParams.get('nextPage') ?? pathname + search + hash;

    localStorage.clear();
    queryClient.clear();

    // biome-ignore lint/suspicious/noExplicitAny: bypass
    return <Navigate to='/login' search={{ nextPage } as any} replace />;
  }

  return <PrivateRouteInternal />;
};

const PrivateRouteInternal = () => {
  const { accessToken, employeeKey, deviceKey, impersonatedCompanyKeys, impersonatedRole } = useAuth();
  if (!accessToken || !employeeKey || !deviceKey) throw new Error('No access credentials'); // Internal component should never be rendered without access credentials

  const queryClient = useQueryClient();

  useEffect(() => {
    initializeLogRocket();
  }, []);

  useIntercom();

  const { data: authUser } = useEmployeeQuery(employeeKey);

  const defaultCompanyKey = authUser?.companyKeys?.[0];
  const { data: defaultUserCompany } = useCompanyQuery(defaultCompanyKey ?? '', {
    enabled: Boolean(defaultCompanyKey),
  });
  const { data: firstCompanyKey } = useFirstCompanyKeyQuery({
    enabled: Boolean(authUser),
  });
  const { data: firstUserCompany } = useCompanyQuery(firstCompanyKey);

  const { data: companiesWithEmployeePrefsEnabled = [], isLoading: isLoadingCompaniesWithEmployeePrefs } =
    useCompaniesWithEmployeePreferencesQuery();

  const userImpersonatedCompanyKeys =
    (authUser?.impersonatedCompanyKeys?.length ?? 0) === 0 ? null : authUser?.impersonatedCompanyKeys?.toString();

  const userImpersonatedRole =
    !authUser?.impersonatedRole || authUser.impersonatedRole === 'superadmin' ? null : authUser.impersonatedRole;

  const { pathname } = useLocation();
  // biome-ignore lint/suspicious/noExplicitAny: bypass
  const searchParams = useSearch({ strict: false }) as any;

  const { width } = useWindowDimensions();

  const isRoomReview = pathname.split('/')[3] === 'rooms' && !!searchParams?.key;
  const isCollapsed = width <= SIDEBAR_OPEN_THRESHOLD || isRoomReview;

  useEffect(() => {
    if (!authUser?.key) return;

    if (impersonatedCompanyKeys !== userImpersonatedCompanyKeys || impersonatedRole !== userImpersonatedRole) {
      updateAuth({
        impersonatedCompanyKeys: userImpersonatedCompanyKeys,
        impersonatedRole: userImpersonatedRole,
      });

      queryClient.removeQueries({ queryKey: [COMPANY_QUERY_KEY, LIST_KEY] });
      queryClient.removeQueries({ queryKey: [EMPLOYEE_QUERY_KEY, LIST_KEY] });
      queryClient.invalidateQueries({
        queryKey: [COMPANY_QUERY_KEY, FIRST_COMPANY_KEY],
      });
    } else {
      initAuthUser(authUser.key, queryClient);
    }
  }, [
    authUser?.key,
    impersonatedCompanyKeys,
    impersonatedRole,
    queryClient,
    userImpersonatedCompanyKeys,
    userImpersonatedRole,
  ]);

  useAutomaticLogout();

  const isAuthLoaded =
    authUser && (!defaultCompanyKey || defaultUserCompany) && firstUserCompany && !isLoadingCompaniesWithEmployeePrefs;

  if (!isAuthLoaded)
    return (
      <>
        <SideNav isLoading isCollapsed={isCollapsed} />
        <LoadingAnimation />
      </>
    );

  return (
    <AuthUserContext.Provider
      value={{
        authUser,
        defaultUserCompany,
        firstUserCompany,
        deviceKey,
        activeRole: authUser.impersonatedRole || authUser.role,
        companiesWithEmployeePrefsEnabled,
      }}
    >
      <Outlet />
    </AuthUserContext.Provider>
  );
};

const useIntercom = () => {
  const location = useLocation();

  useEffect(() => {
    return () => {
      if (window.Intercom) {
        // https://developers.intercom.com/installing-intercom/docs/intercom-javascript#intercomshutdown
        window.Intercom('shutdown');
      }
    };
  }, []);

  useEffect(() => {
    if (window.Intercom) {
      // source: https://www.intercom.com/help/en/articles/170-integrate-intercom-in-a-single-page-app
      window.Intercom('update', {
        last_request_at: new Date().getTime() / 1000,
      });

      // Accept request hides launcher, so unhide it here
      window.Intercom('update', {
        hide_default_launcher: false,
      });
    }
  }, [location]);
};
